import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _get from "@babel/runtime/helpers/get";
import _inherits from "@babel/runtime/helpers/inherits";
import * as THREE from 'three';
import View, { VIEW_EVENTS } from "../View";
import GlobeControls from "../../Controls/GlobeControls";
import GlobeLayer from "./Globe/GlobeLayer";
import Atmosphere from "./Globe/Atmosphere";
import Coordinates from "../Geographic/Coordinates";
import CRS from "../Geographic/Crs";
import { ellipsoidSizes } from "../Math/Ellipsoid";
/**
 * Fires when the view is completely loaded. Controls and view's functions can be called then.
 * @event GlobeView#initialized
 * @property target {view} dispatched on view
 * @property type {string} initialized
 */

/**
 * Fires when a layer is added
 * @event GlobeView#layer-added
 * @property layerId {string} the id of the layer
 * @property target {view} dispatched on view
 * @property type {string} layers-added
 */

/**
 * Fires when a layer is removed
 * @event GlobeView#layer-removed
 * @property layerId {string} the id of the layer
 * @property target {view} dispatched on view
 * @property type {string} layers-added
 */

/**
 * Fires when the layers oder has changed
 * @event GlobeView#layers-order-changed
 * @property new {object}
 * @property new.sequence {array}
 * @property new.sequence.0 {number} the new layer at position 0
 * @property new.sequence.1 {number} the new layer at position 1
 * @property new.sequence.2 {number} the new layer at position 2
 * @property previous {object}
 * @property previous.sequence {array}
 * @property previous.sequence.0 {number} the previous layer at position 0
 * @property previous.sequence.1 {number} the previous layer at position 1
 * @property previous.sequence.2 {number} the previous layer at position 2
 * @property target {view} dispatched on view
 * @property type {string} layers-order-changed
 */

/**
 * Globe's EVENT
 * @property GLOBE_INITIALIZED {string} Deprecated: emit one time when globe is initialized (use VIEW_EVENTS.INITIALIZED instead).
 * @property LAYER_ADDED {string} Deprecated: emit when layer id added in viewer (use VIEW_EVENTS.LAYER_ADDED instead).
 * @property LAYER_REMOVED {string} Deprecated: emit when layer id removed in viewer (use VIEW_EVENTS.LAYER_REMOVED instead).
 * @property COLOR_LAYERS_ORDER_CHANGED {string} Deprecated: emit when  color layers order change (use VIEW_EVENTS.COLOR_LAYERS_ORDER_CHANGED instead).
 */

export var GLOBE_VIEW_EVENTS = {
  GLOBE_INITIALIZED: VIEW_EVENTS.INITIALIZED,
  LAYER_ADDED: VIEW_EVENTS.LAYER_ADDED,
  LAYER_REMOVED: VIEW_EVENTS.LAYER_REMOVED,
  COLOR_LAYERS_ORDER_CHANGED: VIEW_EVENTS.COLOR_LAYERS_ORDER_CHANGED
};

var GlobeView =
/*#__PURE__*/
function (_View) {
  _inherits(GlobeView, _View);

  /**
   * Creates a view of a globe.
   *
   * @constructor
   *
   * @example
   * var viewerDiv = document.getElementById('viewerDiv');
   * var position = new itowns.Coordinates('WGS84', 2.35, 48.8, 25e6);
   * var view = new itowns.GlobeView(viewerDiv, position);
   *
   * @example
   * var viewerDiv = document.getElementById('viewerDiv');
   * var position = { longitude: 2.35, latitude: 48.8, altitude: 25e6 };
   * var view = new itowns.GlobeView(viewerDiv, position);
   *
   * @param {HTMLDivElement} viewerDiv - Where to attach the view and display it
   * in the DOM.
   * @param {object|Coordinates} coordCarto - An object containing three
   * properties: longitude, latitude and altitude. It will help placing the camera
   * on the globe at the creation.
   * @param {object=} options - See options of {@link View}.
   */
  function GlobeView(viewerDiv, coordCarto) {
    var _this;

    var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

    _classCallCheck(this, GlobeView);

    THREE.Object3D.DefaultUp.set(0, 0, 1); // Setup View

    _this = _possibleConstructorReturn(this, _getPrototypeOf(GlobeView).call(this, 'EPSG:4978', viewerDiv, options)); // Configure camera

    var positionCamera;

    if (coordCarto.isCoordinates) {
      positionCamera = coordCarto.as('EPSG:4326');
    } else {
      positionCamera = new Coordinates('EPSG:4326', coordCarto.longitude, coordCarto.latitude, coordCarto.altitude);
    }

    _this.camera.camera3D.near = Math.max(15.0, 0.000002352 * ellipsoidSizes.x);
    _this.camera.camera3D.far = ellipsoidSizes.x * 10;
    var tileLayer = new GlobeLayer('globe', options.object3d, options);
    var sun = new THREE.DirectionalLight();
    sun.position.set(-0.5, 0, 1);
    sun.updateMatrixWorld(true);
    tileLayer.object3d.add(sun);

    _this.addLayer(tileLayer); // Configure controls


    var positionTargetCamera = positionCamera.clone();
    positionTargetCamera.altitude = 0;

    if (options.noControls) {
      _this.camera.setPosition(positionCamera);

      _this.camera.camera3D.lookAt(positionTargetCamera.as('EPSG:4978').toVector3());
    } else {
      _this.controls = new GlobeControls(_assertThisInitialized(_this), positionTargetCamera, positionCamera.altitude, ellipsoidSizes.x);
      _this.controls.handleCollision = typeof options.handleCollision !== 'undefined' ? options.handleCollision : true;
    }

    _this.tileLayer = tileLayer;

    _this.addLayer(new Atmosphere()); // GlobeView needs this.camera.resize to set perpsective matrix camera


    _this.camera.resize(viewerDiv.clientWidth, viewerDiv.clientHeight);

    return _this;
  }

  _createClass(GlobeView, [{
    key: "addLayer",
    value: function addLayer(layer) {
      if (!layer) {
        return new Promise(function (resolve, reject) {
          return reject(new Error('layer is undefined'));
        });
      }

      if (layer.isColorLayer) {
        var colorLayerCount = this.getLayers(function (l) {
          return l.isColorLayer;
        }).length;
        layer.sequence = colorLayerCount;

        if (!this.tileLayer.tileMatrixSets.includes(CRS.formatToTms(layer.source.projection))) {
          throw new Error("Only ".concat(this.tileLayer.tileMatrixSets, " tileMatrixSet are currently supported for color layers"));
        }
      } else if (layer.isElevationLayer) {
        if (CRS.formatToTms(layer.source.projection) !== this.tileLayer.tileMatrixSets[0]) {
          throw new Error("Only ".concat(this.tileLayer.tileMatrixSets[0], " tileMatrixSet is currently supported for elevation layers"));
        }
      }

      return _get(_getPrototypeOf(GlobeView.prototype), "addLayer", this).call(this, layer, this.tileLayer);
    }
  }]);

  return GlobeView;
}(View);

export default GlobeView;